/**
 * Deposit into a tenant's balance, on the operator's behalf.
 *
 * The console's counterpart of the customer's own bank-transfer top-up, and deliberately the
 * same shape: enter an amount, open the payment, read back the account and the `WIC-PAY-…`
 * reference, attach the transfer slip. What it does NOT do is move money. It creates exactly what
 * the customer's screen creates — a `Payment` at `AwaitingVerification` and the `BalanceTopUp`
 * order that records it — and the balance is credited only when that payment is verified from the
 * payment queue, through the same handler and the same wallet service as every other settlement.
 * The payment is linked to that order like any other, so what decides the money reaches the wallet
 * rather than a purchase is the order's TYPE, not the absence of an order.
 *
 * That is the reason this exists next to the manual balance adjustment rather than replacing it:
 * an adjustment is a bare credit with a reason, while this leaves a reference an incoming
 * transfer can be reconciled against, a slip on file, and a second pair of eyes on the approval.
 *
 * A deposit can also be opened as a T-PayGate QR bill (2026-10-12). The method is always offered
 * — unlike the customer console, which asks the server whether QR is available — and a platform
 * with T-PayGate switched off simply answers the create request with a refusal. A QR deposit has
 * no slip and no verification here: the gateway's webhook credits the wallet when the exact amount
 * arrives, and this screen polls the payment until it does.
 *
 * Forward-only once the payment exists. The client screen lets a customer go back and change the
 * figure because it can quietly cancel the payment it opened; doing that here would push a
 * "payment rejected" notification to a customer who was never told a payment had been opened. A
 * wrong figure is corrected where the consequence is visible — on the payment itself.
 */

import {
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ExternalLink,
  FileText,
  HelpCircle,
  Landmark,
  Loader2,
  QrCode,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { PageHeader } from "@/components/common/page-header";
import { BankAccountInfo } from "@/components/payments/BankAccountInfo";
import { QrBillInfo } from "@/components/payments/QrBillInfo";
import { BusyOverlay } from "@/components/common/BusyOverlay";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  tenantKeys,
  useTenantBilling,
  useTenantDetail,
} from "@/hooks/useTenantQueries";
import { adminBankTransferConfigService } from "@/services/admin-bank-transfer-config.service";
import {
  adminPaymentService,
  type PaymentVerifiedDto,
} from "@/services/admin-payment.service";
import { adminTenantDepositService } from "@/services/admin-tenant-deposit.service";
import {
  VerificationModal,
  type VerificationModalPayment,
} from "@/components/payments/VerificationModal";
import { adminUploadService } from "@/services/admin-upload.service";
import {
  caretAfterFormat,
  formatAmountInput,
  isGroupSeparator,
  parseAmountInput,
} from "@/lib/amountInput";
import { formatCurrency, getCurrencySymbol } from "@/lib/currency";
import { parsePaymentError } from "@/lib/errorUtils";
import { awaitRefresh } from "@/lib/awaitRefresh";
import { cn } from "@/lib/utils";
import {
  ACCEPTED_UPLOAD_TYPES,
  partitionValidUploads,
} from "@/lib/uploadValidation";
import type {
  CreateTenantDepositResponse,
  TenantDepositMethod,
} from "@/types/tenant-deposit";

type DepositStep = "amount" | "bank-info" | "upload-proof" | "success" | "qr";

/**
 * The two ways a deposit can be opened, in the order they are offered. Bank transfer stays first
 * and preselected: it is what this screen did before QR was added.
 */
const DEPOSIT_METHODS: {
  value: TenantDepositMethod;
  label: string;
  hint: string;
  icon: typeof Landmark;
}[] = [
  {
    value: "BankTransfer",
    label: "Bank transfer",
    hint: "Attach the slip, then verify. Credited on verification.",
    icon: Landmark,
  },
  {
    value: "QRCode",
    label: "QR code (T-PayGate)",
    hint: "The customer scans a VietQR bill. Credited automatically.",
    icon: QrCode,
  },
];

/** How often an open QR bill is re-read, the same pace as the customer's own QR screen. */
const QR_POLL_INTERVAL_MS = 5_000;

/**
 * Wraps each picked `File` with a stable id assigned at pick time.
 *
 * `File` objects carry no identity of their own, and the browser lets an operator pick the same
 * file twice (the input is cleared after every change so a repeat pick still fires) — this screen
 * does not dedupe, so `name`/`size`/`lastModified` can collide across two entries. Keying (and
 * removing) by that composite, or by array index, then targets the wrong row once one file is
 * removed while another is still uploading. An id minted once per pick has no such collision.
 */
interface PickedFile {
  id: string;
  file: File;
}

let pickedFileCounter = 0;

function nextPickedFileId(): string {
  // crypto.randomUUID is available in every supported browser and in the test environment's
  // Node runtime, but fall back to a monotonic counter rather than assume it everywhere.
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `picked-${++pickedFileCounter}`;
}

/** Matches AttachPaymentProofCommandHandler.MaxProofAttachments. */
const MAX_PROOF_FILES = 10;

/**
 * The customer console's production floor (`DEFAULT_MIN_DEPOSIT_VND`), applied here too so an
 * operator cannot open a deposit the customer's own screen would have refused.
 *
 * đồng-shaped, and so are the quick amounts: both are skipped for a wallet in another currency,
 * where ₫10,000 is not a floor but roughly $10,000. The server's own rule (greater than zero)
 * then stands alone — the same rule the customer path falls back on outside VND.
 */
const MIN_DEPOSIT_VND = 10_000;

/** Matches CreateTenantDepositCommandValidator. */
const MAX_DEPOSIT = 1_000_000_000;

const QUICK_AMOUNTS_VND = [
  50_000, 100_000, 200_000, 500_000, 1_000_000, 2_000_000,
];

export function TenantDepositPage() {
  const navigate = useNavigate();
  const { id: tenantId = "" } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<TenantDepositMethod>("BankTransfer");
  const [note, setNote] = useState("");
  const [validationError, setValidationError] = useState("");
  const [step, setStep] = useState<DepositStep>("amount");
  const [deposit, setDeposit] = useState<CreateTenantDepositResponse | null>(
    null,
  );
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [uploading, setUploading] = useState(false);

  /**
   * Verification, done here rather than from the Payments menu.
   *
   * It is still the same `POST /admin/payments/{id}/verify` and the same
   * `VerifyPaymentCommandHandler` that credits the wallet — nothing here shortcuts the approval,
   * and the operator is still asked to type the figure that actually arrived. What is removed is
   * the trip to another screen to find a payment they opened thirty seconds ago.
   *
   * `verifyPayment` refuses to credit against a payment with no proof, so the button only appears
   * once the slip has been filed.
   */
  const [verifyModalPayment, setVerifyModalPayment] =
    useState<VerificationModalPayment | null>(null);
  const [openingVerify, setOpeningVerify] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState<PaymentVerifiedDto | null>(null);

  const amountRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * A create request is outstanding.
   *
   * `mutation.isPending` cannot carry this: it only flips on the NEXT render, so two clicks
   * landing in the same tick both read `false` and the tenant ends up with two references for
   * one transfer. A ref changes synchronously, so the second click sees the first.
   */
  const createInFlight = useRef(false);
  /** Same hazard on the submit button, where it would attach the same slip twice. */
  const proofInFlight = useRef(false);

  const {
    data: tenant,
    isLoading: tenantLoading,
    error: tenantError,
    refetch: refetchTenant,
  } = useTenantDetail(tenantId);

  /**
   * The wallet's own currency, read before anything is typed.
   *
   * The server refuses to credit a top-up in a currency the wallet does not hold, so it always
   * denominates the payment in the wallet's currency — this only makes the amount field agree
   * with it in advance, rather than showing a ₫ prefix to an operator topping up a USD account.
   * VND until the summary arrives, and for a tenant who has never held a balance.
   */
  const {
    data: billing,
    isLoading: billingLoading,
    error: billingError,
    refetch: refetchBilling,
  } = useTenantBilling(tenantId);
  const walletCurrency = deposit?.currency || billing?.currency || "VND";
  const isVndWallet = walletCurrency.toUpperCase() === "VND";
  // A VietQR bill carries only a number of đồng, so the server refuses QR for any other wallet
  // (QR_CURRENCY_NOT_SUPPORTED); the option is disabled rather than left to fail.
  const effectiveMethod: TenantDepositMethod =
    method === "QRCode" && isVndWallet ? "QRCode" : "BankTransfer";
  // Only a bank transfer quotes the platform's account, so only it waits on that setting.
  const needsBankAccount = effectiveMethod === "BankTransfer";

  // Fetched before the payment exists, so the operator is never sent to a step that would show
  // placeholder account details. The create response carries the same account, but only after
  // the payment has been opened.
  const {
    data: bankConfig,
    isLoading: bankConfigLoading,
    error: bankConfigError,
    refetch: refetchBankConfig,
  } = useQuery({
    // The same key the settings tab uses, so an operator who has just edited the account here
    // sees the edited one rather than a second copy fetched under a key of its own.
    queryKey: ["admin", "platform-settings", "bank-transfer-config"],
    queryFn: () => adminBankTransferConfigService.getBankTransferConfig(),
    staleTime: 60_000,
  });

  /**
   * Top-ups already open for this tenant, checked before a new one is opened.
   *
   * The in-flight ref below stops a double click, but not an operator who opened this screen,
   * walked away, and came back — or two operators on the same customer. Neither the server nor
   * the customer's own screen forbids a second top-up, and it should not: a customer can genuinely
   * have two transfers in the air. What must not happen silently is a second WIC-PAY reference for
   * ONE incoming transfer, because then neither reference reconciles against the bank statement.
   * So this warns and names the open ones rather than blocking.
   *
   * Order-linked payments are filtered out: those are VM purchases waiting for a slip, not
   * top-ups, and warning about them would train operators to ignore the banner.
   */
  const {
    data: openDeposits,
    error: openDepositsError,
    refetch: refetchOpenDeposits,
  } = useQuery({
    queryKey: ["admin", "payments", "open-deposits", tenantId],
    queryFn: () =>
      adminPaymentService.getPayments({
        tenantId,
        status: "AwaitingVerification",
        pageSize: 20,
      }),
    enabled: Boolean(tenantId),
    staleTime: 30_000,
  });

  const openTopUps = (openDeposits?.data ?? []).filter(
    (payment) => !payment.orderId && payment.paymentId !== deposit?.paymentId,
  );

  // `current` is the runtime source of truth (`deployed` is the value waiting in config).
  const configuredAccount = bankConfig?.current;
  const isBankConfigured = Boolean(
    configuredAccount?.bankName && configuredAccount?.accountNumber,
  );

  const amountValue = () => parseAmountInput(amount) ?? 0;

  const createMutation = useMutation({
    mutationFn: (value: number) =>
      adminTenantDepositService.createDeposit(tenantId, {
        amount: value,
        note: note.trim() || undefined,
        paymentMethod: effectiveMethod,
      }),
    onSuccess: (response) => {
      setDeposit(response);
      if (response.qrBill) {
        setStep("qr");
        toast.success("QR bill issued", {
          description: `Reference ${response.paymentOrderNumber}. The balance is credited when the transfer arrives.`,
        });
        return;
      }
      setStep("bank-info");
      toast.success("Deposit opened", {
        description: `Reference ${response.paymentOrderNumber}. Nothing is credited until the payment is verified.`,
      });
    },
    onError: (error: unknown) => {
      toast.error(parsePaymentError(error).message);
    },
    // Released on failure too, or a network blip locks the button for the rest of the visit.
    onSettled: () => {
      createInFlight.current = false;
    },
  });

  const handleAmountKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Steps the caret over a group separator before the browser deletes anything: deleting a
    // separator on its own is a no-op (the mask re-inserts it), so one keypress would otherwise
    // appear to do nothing. Only for a collapsed caret — a selection is already unambiguous.
    const field = event.currentTarget;
    const start = field.selectionStart;
    if (start === null || start !== field.selectionEnd) {
      return;
    }

    if (event.key === "Backspace" && isGroupSeparator(field.value[start - 1])) {
      field.setSelectionRange(start - 1, start - 1);
    } else if (event.key === "Delete" && isGroupSeparator(field.value[start])) {
      field.setSelectionRange(start + 1, start + 1);
    }
  };

  const handleAmountInput = (event: ChangeEvent<HTMLInputElement>) => {
    const typed = event.target.value;
    const caret = event.target.selectionStart ?? typed.length;
    const formatted = formatAmountInput(typed);

    setAmount(formatted);
    setValidationError("");

    // Restored in a frame, not inline: React has not written the new value to the DOM node yet,
    // and setting the selection against the old string lands it somewhere else entirely.
    const next = caretAfterFormat(typed, caret, formatted);
    requestAnimationFrame(() => {
      amountRef.current?.setSelectionRange(next, next);
    });
  };

  const handleCreate = () => {
    const value = amountValue();

    if (value <= 0) {
      setValidationError("Enter an amount greater than zero.");
      return;
    }

    if (isVndWallet && value < MIN_DEPOSIT_VND) {
      setValidationError(
        `The smallest deposit is ${formatCurrency(MIN_DEPOSIT_VND, walletCurrency)}.`,
      );
      return;
    }

    if (value > MAX_DEPOSIT) {
      setValidationError(
        `The largest deposit is ${formatCurrency(MAX_DEPOSIT, walletCurrency)}.`,
      );
      return;
    }

    if (needsBankAccount && !isBankConfigured) {
      toast.error("Bank transfer is not configured", {
        description:
          "Set the platform bank account under Settings → Bank Transfer before opening a deposit.",
      });
      return;
    }

    if (createInFlight.current) {
      return;
    }

    createInFlight.current = true;
    createMutation.mutate(value);
  };

  const handleFilesPicked = (picked: FileList | null) => {
    const { valid, errors } = partitionValidUploads(Array.from(picked ?? []));
    errors.forEach((message) => toast.error(message));

    if (valid.length === 0) {
      return;
    }

    setFiles((current) => {
      const room = MAX_PROOF_FILES - current.length;
      if (room <= 0) {
        toast.error(`A payment can carry at most ${MAX_PROOF_FILES} files.`);
        return current;
      }

      if (valid.length > room) {
        toast.error(
          `Only ${room} more file${room === 1 ? "" : "s"} can be attached to this payment.`,
        );
      }

      const picked = valid
        .slice(0, room)
        .map((file) => ({ id: nextPickedFileId(), file }));
      return [...current, ...picked];
    });
  };

  const handleSubmitProof = async () => {
    // The ref, not `uploading`: state flips a render too late to stop the second of two clicks
    // landing in the same tick, and that pair attaches the same slip twice.
    if (proofInFlight.current || !deposit) {
      return;
    }

    if (files.length === 0) {
      toast.error("Attach the transfer slip before submitting.");
      return;
    }

    proofInFlight.current = true;
    setUploading(true);

    try {
      // In parallel, not one at a time: sequential uploads under a page-blocking overlay meant
      // ten files at up to 300s each could hold the veil up for the better part of an hour.
      // `allSettled` rather than `all` — one bad file must not stop the others from finishing, or
      // a single corrupt slip in a batch of ten would throw the other nine away with it.
      const results = await Promise.allSettled(
        files.map(({ file }) => adminUploadService.uploadFile(file)),
      );

      const uploadedIds: string[] = [];
      const failedFileNames: string[] = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          uploadedIds.push(result.value.attachmentId);
        } else {
          failedFileNames.push(files[index].file.name);
        }
      });

      if (failedFileNames.length > 0) {
        // Uploaded ids are cleaned up so a partial batch can't leave orphans — a file uploaded
        // but never linked is invisible to every screen and would sit in storage forever. The
        // whole submission is refused rather than attaching the ones that made it: silently
        // filing an incomplete slip is worse than asking the operator to retry the batch.
        await Promise.allSettled(
          uploadedIds.map((id) => adminUploadService.removeUnusedUpload(id)),
        );
        toast.error(
          `Failed to upload: ${failedFileNames.join(", ")}. Nothing was attached — try again.`,
        );
        return;
      }

      try {
        await adminPaymentService.attachPaymentProof(
          deposit.paymentId,
          uploadedIds,
        );
        setStep("success");
      } catch (error) {
        // Every upload made it, but the link did not — the same orphan risk as a partial batch,
        // so the same cleanup applies.
        await Promise.allSettled(
          uploadedIds.map((id) => adminUploadService.removeUnusedUpload(id)),
        );
        toast.error(parsePaymentError(error).message);
      }
    } finally {
      proofInFlight.current = false;
      setUploading(false);
    }
  };

  /**
   * Reads the payment back before opening the dialog.
   *
   * The dialog compares the operator's entry against `Payment.Amount` as the payment DTOs carry
   * it, so the figure has to come from the payment rather than from the create response — the two
   * agree for VND and would not for a currency with decimals. The read also brings the proof files
   * along, which is what the operator is being asked to check the amount against.
   */
  const handleOpenVerify = async () => {
    if (!deposit) {
      return;
    }

    setOpeningVerify(true);
    try {
      const payment = await adminPaymentService.getPaymentDetails(
        deposit.paymentId,
      );

      setVerifyModalPayment({
        paymentOrderNumber: payment.paymentOrderNumber,
        amount: payment.amount,
        currency: payment.currency,
        qrProvider: payment.qrProvider,
        proofs: payment.attachments,
      });
    } catch (error) {
      toast.error(parsePaymentError(error).message);
    } finally {
      setOpeningVerify(false);
    }
  };

  const handleVerify = async (
    notes: string | undefined,
    receivedAmount: number,
  ) => {
    if (!deposit) {
      return;
    }

    setVerifying(true);
    try {
      const result = await adminPaymentService.verifyPayment(
        deposit.paymentId,
        {
          notes: notes ?? "Verified from the tenant deposit screen.",
          receivedAmount,
        },
      );

      // The cached billing summary, the tenant list and the payment itself all hold the
      // pre-credit state, and the list (and the payment detail link on this page) is where the
      // operator can land next. Awaited, not fire-and-forget: closing the modal and flipping to
      // the verified screen before these land would let the operator navigate away on stale data
      // the moment they click through. Capped by `awaitRefresh` so a stalled re-read cannot hold
      // the modal's own busy state up indefinitely — past the cap the refetch keeps running and
      // the screen catches up when it lands.
      await awaitRefresh(
        Promise.all([
          queryClient.invalidateQueries({ queryKey: tenantKeys.billing(tenantId) }),
          queryClient.invalidateQueries({ queryKey: tenantKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: ["payments"] }),
        ]),
      );

      setVerifyModalPayment(null);
      setVerified(result);
      toast.success("Payment verified — the balance has been credited.");
    } catch (error) {
      // AMOUNT_MISMATCH lands here: the server refuses rather than crediting a figure the
      // operator did not confirm, and the dialog stays open so the entry can be corrected.
      toast.error(parsePaymentError(error).message);
    } finally {
      setVerifying(false);
    }
  };

  /**
   * The open QR bill, re-read until it is settled or closed.
   *
   * The webhook credits the wallet on the server; nothing pushes that to this screen, so it asks —
   * `GET /admin/payments/{id}`, every 5 s while the bill is `Pending` or `AwaitingVerification`.
   * A re-read that fails leaves the code on screen; the next one is 5 s later.
   */
  const isQrStep = step === "qr" && Boolean(deposit?.qrBill);
  const { data: qrPayment } = useQuery({
    queryKey: ["admin", "payments", "deposit-qr", deposit?.paymentId],
    queryFn: () => adminPaymentService.getPaymentDetails(deposit?.paymentId ?? ""),
    enabled: isQrStep,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const stillOpen =
        !status || status === "Pending" || status === "AwaitingVerification";
      return stillOpen ? QR_POLL_INTERVAL_MS : false;
    },
  });
  const qrStatus = qrPayment?.status ?? deposit?.status;
  const qrOpen = qrStatus === "Pending" || qrStatus === "AwaitingVerification";
  const qrSettled = isQrStep && qrStatus === "Completed";
  // Anything else can no longer be paid: cancelled, rejected (`Failed`), or refunded since.
  const qrClosed = isQrStep && !qrOpen && !qrSettled;
  let qrClosedAs = "closed";
  if (qrStatus === "Cancelled") {
    qrClosedAs = "cancelled";
  } else if (qrStatus === "Failed") {
    qrClosedAs = "rejected";
  }
  const settledPaymentId = deposit?.paymentId;

  // The cached billing summary, the tenant list and the payment lists all hold the pre-credit
  // state — the same three the manual verification refreshes.
  useEffect(() => {
    if (!qrSettled) {
      return;
    }
    // One id, so a remount cannot announce the same settlement twice.
    toast.success("Payment received — the balance has been credited.", {
      id: `deposit-qr-settled-${settledPaymentId}`,
    });
    void queryClient.invalidateQueries({ queryKey: tenantKeys.billing(tenantId) });
    void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
    void queryClient.invalidateQueries({ queryKey: ["payments"] });
  }, [qrSettled, settledPaymentId, queryClient, tenantId]);

  const tenantLabel = deposit?.tenantName ?? tenant?.name ?? "";
  // What the banner under the header says about the payment, once one exists.
  let depositState =
    "is open and waiting for verification. Nothing has been credited yet.";
  if (verified) {
    depositState = "has been verified and credited.";
  } else if (qrSettled) {
    depositState = "has been paid and credited.";
  } else if (qrClosed) {
    depositState = "was closed without being paid. Nothing was credited.";
  } else if (isQrStep) {
    depositState =
      "is open and waiting for the transfer. Nothing has been credited yet.";
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Deposit to tenant balance"
        subtitle={
          tenantLabel
            ? `Top-up for ${tenantLabel}`
            : "Top-up on the customer's behalf"
        }
        back={{
          label: "Back to tenants",
          // PageHeader's back link has no `disabled` slot, so the guard lives in the handler:
          // leaving mid-write abandons a create or an upload the operator has no way to check on
          // from the tenant list.
          onClick: () => {
            if (!createMutation.isPending && !uploading) {
              navigate("/admin/tenants");
            }
          },
        }}
        action={<HelpPopover />}
      />

      <div className="mx-auto w-full max-w-2xl space-y-6">
        {/* The reference outlives this screen: once the payment exists it is in the queue whether
            the operator finishes here or closes the tab, so it is on screen from that point on. */}
        {deposit && (
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              Payment{" "}
              <Link
                to={`/admin/payments/${deposit.paymentId}`}
                className="font-mono font-medium text-brand hover:underline"
              >
                {deposit.paymentOrderNumber}
              </Link>{" "}
              for {tenantLabel} {depositState}
            </AlertDescription>
          </Alert>
        )}

        {step === "amount" && (
          <Card>
            <CardHeader>
              <CardTitle>Deposit amount</CardTitle>
              <CardDescription>
                {tenantLoading ? (
                  <Skeleton className="h-4 w-56" />
                ) : (
                  <>
                    {needsBankAccount
                      ? `Opens a bank-transfer payment for ${tenantLabel || "this tenant"}. The balance is credited when the payment is verified.`
                      : `Issues a QR bill for ${tenantLabel || "this tenant"}. The balance is credited when the transfer arrives.`}
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* The name is cosmetic, but a failed read here is the same request that would
                  also carry the tenant's own currency elsewhere, so it is worth its own retry
                  rather than silently falling back to "this tenant" forever. */}
              {!tenantLoading && tenantError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    <p>Could not load this tenant's details.</p>
                    <Button size="sm" variant="outline" onClick={() => refetchTenant()}>
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="deposit-amount">Amount</Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">
                    {getCurrencySymbol(walletCurrency)}
                  </span>
                  <Input
                    id="deposit-amount"
                    ref={amountRef}
                    // Not type="number": it refuses the group separator, and a value the browser
                    // considers invalid comes back as an empty string.
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder={
                      needsBankAccount
                        ? "Enter the amount transferred"
                        : "Enter the amount to collect"
                    }
                    value={amount}
                    onChange={handleAmountInput}
                    onKeyDown={handleAmountKeyDown}
                    disabled={createMutation.isPending}
                    className="pl-10 tabular-nums"
                  />
                </div>
                {validationError && (
                  <p className="text-sm text-destructive">{validationError}</p>
                )}
              </div>

              {isVndWallet && (
                <div className="space-y-2">
                  <Label>Quick amounts</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {QUICK_AMOUNTS_VND.map((quick) => (
                      <Button
                        key={quick}
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={createMutation.isPending}
                        onClick={() => {
                          setAmount(formatAmountInput(String(quick)));
                          setValidationError("");
                        }}
                      >
                        {(quick / 1000).toFixed(0)}k
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label id="deposit-method-label">Payment method</Label>
                <div
                  role="radiogroup"
                  aria-labelledby="deposit-method-label"
                  className="grid gap-2 sm:grid-cols-2"
                >
                  {DEPOSIT_METHODS.map((option) => {
                    const Icon = option.icon;
                    const selected = effectiveMethod === option.value;
                    const unavailable =
                      option.value === "QRCode" && !isVndWallet;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={createMutation.isPending || unavailable}
                        onClick={() => setMethod(option.value)}
                        className={cn(
                          "flex cursor-pointer items-start gap-3 rounded-lg border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                          selected
                            ? "border-primary bg-brand-soft"
                            : "border-hairline hover:bg-panel-2",
                        )}
                      >
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium">
                            {option.label}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {unavailable
                              ? "VND wallets only."
                              : option.hint}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deposit-note">Note (optional)</Label>
                <Textarea
                  id="deposit-note"
                  placeholder="Why this deposit is being opened for the customer."
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  maxLength={500}
                  rows={3}
                  disabled={createMutation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Recorded in the audit trail for operators. The customer never
                  sees it.
                </p>
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {needsBankAccount
                    ? "This does not move money. It opens a bank-transfer payment with its own reference; the balance changes only once that payment is verified from the payment queue."
                    : "This does not move money. It issues a QR bill with its own reference; the balance changes only when T-PayGate confirms a transfer of the exact amount."}
                </AlertDescription>
              </Alert>

              {/* The duplicate-reference warning below depends on this read; a failure here
                  hides it silently rather than blocking the flow, so this is a small notice
                  next to it rather than a full-width alert. */}
              {openDepositsError && (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  Could not check for other open top-ups for this tenant.
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-auto p-0 text-xs underline"
                    onClick={() => refetchOpenDeposits()}
                  >
                    Retry
                  </Button>
                </p>
              )}

              {/* A warning, not a block: two transfers in the air is legitimate, two references
                  for one transfer is not, and only the operator knows which this is. */}
              {openTopUps.length > 0 && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>
                      {tenantLabel || "This tenant"} already has{" "}
                      {openTopUps.length} top-up
                      {openTopUps.length === 1 ? "" : "s"} awaiting verification.
                    </strong>{" "}
                    If the transfer you are recording is one of these, open it
                    instead of starting a second reference for the same money:
                    <ul className="mt-2 space-y-1">
                      {openTopUps.map((payment) => (
                        <li key={payment.paymentId}>
                          <Link
                            to={`/admin/payments/${payment.paymentId}`}
                            className="font-mono text-brand hover:underline"
                          >
                            {payment.paymentOrderNumber}
                          </Link>{" "}
                          —{" "}
                          {formatCurrency(payment.amount, payment.currency)}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* A fetch failure is a different problem from a genuinely empty setting, and the
                  fix is different too — retry this read rather than walk to Settings for an
                  account that may well already be configured. Only shown when there is nothing
                  cached to fall back on; with a config already in hand, a failed background
                  refetch does not need to interrupt the flow. Neither notice is shown for a QR
                  deposit, which quotes no bank account. */}
              {needsBankAccount &&
              !bankConfigLoading &&
              bankConfigError &&
              !bankConfig ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    <p>Could not check the platform bank account.</p>
                    <Button size="sm" variant="outline" onClick={() => refetchBankConfig()}>
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                // Explains why Continue is disabled when no account is set up.
                needsBankAccount &&
                !bankConfigLoading &&
                !isBankConfigured && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No platform bank account is configured, so there is nowhere
                      for the transfer to go. Set one under Settings → Bank
                      Transfer first.
                    </AlertDescription>
                  </Alert>
                )
              )}

              {/* The wallet's currency comes from this read — see the note above the query — so
                  a failure here means the amount field's floor and symbol cannot be trusted.
                  Continue stays blocked until it loads rather than risk applying the VND minimum
                  to a wallet that may not be VND. Shown only when there is no cached billing
                  summary to fall back on — with one in hand, the currency is still trustworthy. */}
              {!billingLoading && billingError && !billing && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    <p>Could not read this tenant's wallet currency.</p>
                    <Button size="sm" variant="outline" onClick={() => refetchBilling()}>
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            <CardFooter>
              <Button
                className="w-full"
                size="lg"
                onClick={handleCreate}
                disabled={
                  !amount ||
                  createMutation.isPending ||
                  billingLoading ||
                  (!!billingError && !billing) ||
                  // Cached data survives a failed refetch (`placeholderData`/prior success), so
                  // only a failure with nothing to fall back on should block Continue — otherwise
                  // one flaky background refetch locks the button on an otherwise-usable page.
                  // None of it applies to a QR deposit, which quotes no bank account.
                  (needsBankAccount &&
                    (bankConfigLoading ||
                      (!!bankConfigError && !bankConfig) ||
                      !isBankConfigured))
                }
              >
                {/* A greyed-out button with the normal label is indistinguishable from a broken
                    one, so the wait says what it is waiting for. */}
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Opening the deposit…
                  </>
                ) : (needsBankAccount && bankConfigLoading) || billingLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {needsBankAccount
                      ? "Checking the bank account…"
                      : "Checking the wallet…"}
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

        {step === "bank-info" && deposit?.bankDetails && (
          <div className="space-y-6">
            <BankAccountInfo
              amount={deposit.amount}
              currency={deposit.currency}
              bankDetails={deposit.bankDetails}
              referenceCode={deposit.paymentOrderNumber}
            />

            <Button
              className="w-full"
              size="lg"
              onClick={() => setStep("upload-proof")}
            >
              The transfer has been made
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>

            {/* The slip often arrives later than the reference does. Leaving now is a normal
                outcome, not an abandoned flow — the payment is already in the queue. */}
            <p className="text-center text-sm text-muted-foreground">
              No slip yet? Leave it — the payment stays in the verification queue
              and the slip can be attached from{" "}
              <Link
                to={`/admin/payments/${deposit.paymentId}`}
                className="text-brand hover:underline"
              >
                the payment
              </Link>{" "}
              at any time.
            </p>
          </div>
        )}

        {isQrStep && deposit?.qrBill && !qrSettled && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="h-5 w-5 text-brand" />
                  Scan to pay
                </CardTitle>
                <CardDescription>
                  Send this code to the customer, or have them open the payment
                  in their own console. No slip and no verification: the balance
                  is credited when T-PayGate confirms the transfer.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {qrClosed ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      This payment was {qrClosedAs}, so the code can no longer
                      be paid. Nothing was credited.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <>
                    <QrBillInfo
                      amount={deposit.amount}
                      currency={deposit.currency}
                      paymentOrderNumber={deposit.paymentOrderNumber}
                      bill={deposit.qrBill}
                    />
                    <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Waiting for the transfer. This page updates on its own.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Leaving is a normal outcome: the bill stays payable and settles without this page. */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate(`/admin/tenants/${tenantId}`)}
              >
                Back to tenant
              </Button>
              <Button
                className="flex-1"
                onClick={() => navigate(`/admin/payments/${deposit.paymentId}`)}
              >
                Open the payment
              </Button>
            </div>
          </div>
        )}

        {isQrStep && deposit && qrSettled && (
          <Card>
            <CardContent className="space-y-6 pt-6">
              <div className="flex flex-col items-center justify-center space-y-4 py-6">
                <CheckCircle className="h-16 w-16 text-ok" />
                <div className="space-y-2 text-center">
                  <h2 className="text-2xl font-bold">Balance credited</h2>
                  <p className="text-muted-foreground">
                    {formatCurrency(deposit.amount, deposit.currency)} has been
                    added to {tenantLabel}'s balance.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate(`/admin/tenants/${tenantId}`)}
                >
                  Back to tenant
                </Button>
                <Button
                  className="flex-1"
                  onClick={() =>
                    navigate(`/admin/payments/${deposit.paymentId}`)
                  }
                >
                  Open the payment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === "upload-proof" && deposit && (
          <div className="space-y-6">
            <Button
              variant="ghost"
              onClick={() => setStep("bank-info")}
              disabled={uploading}
              className="-ml-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to the bank details
            </Button>

            <Card>
              <CardHeader>
                <CardTitle>Transfer slip</CardTitle>
                <CardDescription>
                  PNG, JPG or PDF, up to {MAX_PROOF_FILES} files. The payment
                  cannot be verified without one.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ACCEPTED_UPLOAD_TYPES}
                  className="hidden"
                  onChange={(event) => {
                    handleFilesPicked(event.target.files);
                    // Cleared so picking the same file twice still fires a change event.
                    event.target.value = "";
                  }}
                />

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={uploading || files.length >= MAX_PROOF_FILES}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Choose files
                </Button>

                {files.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground">
                    No file chosen yet.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {files.map(({ id, file }) => (
                      <li
                        // The id minted at pick time, not the file's own name/size/lastModified:
                        // picking the same file twice is allowed and produces two entries with
                        // identical values on those fields, which would collide as a React key
                        // and make removal target the wrong row.
                        key={id}
                        className="flex items-center justify-between rounded-lg border border-hairline bg-panel-2 p-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium" title={file.name}>
                            {file.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(0)} KB
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={uploading}
                          onClick={() =>
                            setFiles((current) =>
                              current.filter((entry) => entry.id !== id),
                            )
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
              <CardFooter className="flex-col gap-3">
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => void handleSubmitProof()}
                  disabled={files.length === 0 || uploading}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    "Submit for verification"
                  )}
                </Button>
                {uploading && (
                  <p className="text-sm text-muted-foreground">
                    Don't close this page while the files are uploading.
                  </p>
                )}
              </CardFooter>
            </Card>
          </div>
        )}

        {step === "success" && deposit && (
          <Card>
            <CardContent className="space-y-6 pt-6">
              <div className="flex flex-col items-center justify-center space-y-4 py-6">
                <CheckCircle className="h-16 w-16 text-ok" />
                <div className="space-y-2 text-center">
                  <h2 className="text-2xl font-bold">
                    {verified ? "Balance credited" : "Slip submitted"}
                  </h2>
                  <p className="text-muted-foreground">
                    {verified
                      ? `${formatCurrency(deposit.amount, deposit.currency)} has been added to ${tenantLabel}'s balance.`
                      : `${formatCurrency(deposit.amount, deposit.currency)} for ${tenantLabel} is waiting for verification.`}
                  </p>
                </div>

                {/* The figure the server actually landed, not the one that was typed — the two
                    differ if anything else moved the wallet between opening and verifying. */}
                {verified && (
                  <div className="w-full rounded-lg border border-ok/30 bg-ok-soft px-6 py-4 text-center">
                    <p className="text-sm font-medium text-ok/80">
                      New balance
                    </p>
                    <p className="text-2xl font-bold tabular-nums text-ok">
                      {formatCurrency(verified.newBalance, deposit.currency)}
                    </p>
                  </div>
                )}
              </div>

              {/* Verification happens here rather than through the Payments menu: the operator
                  opened this payment and filed its slip seconds ago, so approving it is the next
                  click. It is the same endpoint and the same approval — the dialog still asks for
                  the amount that actually arrived, and the server still refuses a figure that
                  does not match (AMOUNT_MISMATCH). Nothing is credited automatically. */}
              {!verified && (
                <Button
                  className="w-full"
                  size="lg"
                  onClick={() => void handleOpenVerify()}
                  disabled={openingVerify || verifying}
                >
                  {openingVerify ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading the payment…
                    </>
                  ) : verifying ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying…
                    </>
                  ) : (
                    "Verify and credit the balance"
                  )}
                </Button>
              )}

              {!verified && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>What happens next</strong>
                    <ol className="mt-2 list-inside list-decimal space-y-1">
                      <li>
                        Check the slip against the bank statement — the money has
                        to have arrived before this is approved.
                      </li>
                      <li>
                        Verify here, or leave it: the payment is in the queue
                        under reference{" "}
                        <span className="font-mono">
                          {deposit.paymentOrderNumber}
                        </span>{" "}
                        for anyone with the rights to approve.
                      </li>
                      <li>
                        The balance is credited and the tenant notified only on
                        verification.
                      </li>
                    </ol>
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => navigate(`/admin/tenants/${tenantId}`)}
                >
                  Back to tenant
                </Button>
                <Button
                  className="flex-1"
                  onClick={() =>
                    navigate(`/admin/payments/${deposit.paymentId}`)
                  }
                >
                  Open the payment
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Only reachable by deep-linking past the amount step, e.g. a reload. */}
        {step !== "amount" && !deposit && (
          <Card>
            <CardContent className="flex flex-col items-center gap-4 pt-6 text-center">
              <X className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                This deposit is no longer in progress. Start again from the
                amount.
              </p>
              <Button variant="outline" onClick={() => setStep("amount")}>
                Start over
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Rendered only once the payment has been read back, so the dialog always checks the
          operator's entry against a real figure rather than a placeholder. */}
      {verifyModalPayment && (
        <VerificationModal
          open
          onOpenChange={(open) => {
            if (!open) {
              setVerifyModalPayment(null);
            }
          }}
          payment={verifyModalPayment}
          onConfirm={handleVerify}
          isLoading={verifying}
        />
      )}

      {/* Verifying already gets its own overlay from VerificationModal (it owns that request and
          the dialog it runs behind) — only the two writes this page owns directly need one here. */}
      <BusyOverlay open={createMutation.isPending} label="Opening the deposit…" />
      <BusyOverlay
        open={uploading}
        label={
          files.length > 1
            ? `Uploading ${files.length} slips…`
            : "Uploading the transfer slip…"
        }
        hint="Large files can take a while. Keep this tab open."
      />
    </div>
  );
}

/**
 * The short answer to "what will this actually do to the customer's money", opened in place.
 *
 * Kept to the one thing an operator has to be sure of before pressing — that opening a deposit
 * moves nothing, and the balance changes only at the approval step. Everything else (the shape of
 * the payment, the refusals, the trail it leaves) lives in the full guide, which is a page rather
 * than more popover.
 */
function HelpPopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm">
          <HelpCircle className="mr-2 h-4 w-4" />
          How this works
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="w-80 space-y-3 text-sm"
      >
        <p className="text-muted-foreground">
          Opens the same top-up the customer would open themselves — for the
          times they ask support to do it, or transferred before any payment
          existed. Use it instead of adjusting the balance whenever real money
          moved.
        </p>

        <div className="space-y-1.5">
          <p className="font-medium">It creates one payment</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>· bank transfer or QR bill, with a WIC-PAY reference</li>
            <li>· raises a top-up order — the money still credits the wallet</li>
            <li>· awaiting verification, nothing credited yet</li>
          </ul>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">
            Bank transfer: the balance moves only when you verify
          </p>
          <p className="text-muted-foreground">
            Same approval as the Payments menu: the slip has to be attached
            first, and the figure you type has to match, or it is refused.
          </p>
        </div>

        <div className="space-y-1.5">
          <p className="font-medium">QR: the balance moves on its own</p>
          <p className="text-muted-foreground">
            T-PayGate confirms the transfer and the wallet is credited — only
            for the exact amount and the transfer content on the code.
          </p>
        </div>

        <Button variant="outline" size="sm" asChild className="w-full">
          <a href="/docs/tenant-deposit.html" target="_blank" rel="noreferrer">
            Full guide
            <ExternalLink className="ml-2 h-3.5 w-3.5" />
          </a>
        </Button>
      </PopoverContent>
    </Popover>
  );
}
